import { useCallback, useEffect, useMemo, useState } from 'react'
import { ServerApiClient } from '../../data/ServerApiClient'
import { downloadOperatingPdf, downloadOperatingPptx, type OperatingArtifactSpec } from './operatingArtifactFiles'
import { formatWorkspaceMoney } from './workspaceFormatting'
import './ClientOperatingWorkspacePage.css'
import './ClientOperatingWorkspaceControls.css'

type WorkspaceWidget = {
  kind: string
  id: string
  title: string
  metric_key?: string
  metric_keys?: string[]
  comparison?: string | null
  goal_keys?: string[]
  suggested_prompts?: string[]
  markdown?: string
  formats?: string[]
}

function routeWorkbookId(): string {
  const parts = window.location.pathname.split('/').filter(Boolean)
  return parts[1] === 'workbooks' ? String(parts[2] || '') : ''
}

function n(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function operatingValue(value: unknown, unit = 'decimal', currency: unknown = 'USD'): string {
  if (value == null) return '—'
  if (unit === 'currency') return formatWorkspaceMoney(value, 'currency', currency)
  if (unit === 'percent' || unit === 'ratio') return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 1 }).format(n(value))
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n(value))
  return unit === 'duration' ? `${formatted} days` : formatted
}

function status(value: string): string {
  return String(value || '').replaceAll('_', ' ')
}

export function ClientOperatingWorkspacePage({ topInsetPx = 0 }: { topInsetPx?: number }) {
  const workbookId = useMemo(routeWorkbookId, [])
  const api = useMemo(() => new ServerApiClient(), [])
  const [context, setContext] = useState<any>(null)
  const [workspace, setWorkspace] = useState<any>(null)
  const [pageId, setPageId] = useState('today')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [answer, setAnswer] = useState<any>(null)
  const [artifactBusy, setArtifactBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!workbookId) return
    setBusy(true)
    setError(null)
    try {
      const [contextResponse, workspaceResponse] = await Promise.all([
        api.getPlatformClientContext(workbookId),
        api.getClientOperatingWorkspace(workbookId),
      ])
      setContext(contextResponse?.data ?? null)
      setWorkspace(workspaceResponse?.data ?? null)
    } catch (caught: any) {
      setError(caught?.message || 'The client operating workspace could not be loaded.')
    } finally {
      setBusy(false)
    }
  }, [api, workbookId])

  useEffect(() => { void load() }, [load])

  const spec = workspace?.published_revision?.spec
  const pages = Array.isArray(spec?.pages) ? spec.pages : []
  const page = pages.find((item: any) => item.id === pageId) ?? pages[0]
  const metrics = Array.isArray(context?.financial_position) ? context.financial_position : []
  const goals = Array.isArray(workspace?.goals) ? workspace.goals : []
  const actions = Array.isArray(workspace?.actions) ? workspace.actions : []
  const metric = (key: string) => metrics.find((item: any) => item.metric_id === key || String(item.label || '').toLowerCase().replaceAll(' ', '_') === key)

  useEffect(() => {
    // Guided tours drive page changes through this event; unknown ids are
    // ignored so the tour engine's own fallback can handle them.
    const controller = new AbortController()
    window.addEventListener('fg_workspace_goto', (event: Event) => {
      const next = String((event as CustomEvent).detail?.page ?? '')
      if (pages.some((item: any) => item.id === next)) setPageId(next)
    }, { signal: controller.signal })
    return () => controller.abort()
  }, [pages])

  useEffect(() => {
    // Re-arm tour overlay anchor resolution after in-workspace navigation.
    window.dispatchEvent(new Event('fg_route_change'))
  }, [pageId])

  const investigate = async (prompt: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await api.invokePlatformCapability('investigate_material_variance', workbookId, {
        comparison: 'mom',
        expected_freshness_token: context?.freshness?.token,
      })
      setAnswer({ prompt, ...result?.data })
    } catch (caught: any) {
      setError(caught?.message || 'The investigation could not be prepared.')
    } finally {
      setBusy(false)
    }
  }

  const updateAction = async (item: any) => {
    const next = item.status === 'open' ? 'in_progress' : item.status === 'in_progress' ? 'done' : 'open'
    setBusy(true)
    try {
      await api.manageClientOperatingWorkspace('manage_action_item', workbookId, {
        operation: 'set_status', action_id: item.id, status: next, expected_updated_at: item.updated_at,
      })
      await load()
    } catch (caught: any) {
      setError(caught?.message || 'The commitment could not be updated.')
      setBusy(false)
    }
  }

  const copyAgentPrompt = async () => {
    const prompt = `Customize the client operating workspace for ${context?.company?.display_name || 'this client'}. Keep the current authorization and live-data contracts. Start by asking me what this management team owns, which weekly goals matter, and what should appear on Today. Then publish the revision directly when I approve it.`
    await navigator.clipboard.writeText(prompt)
  }

  const postCheckIn = async (event: React.FormEvent<HTMLFormElement>, goal: any) => {
    event.preventDefault()
    const values = new FormData(event.currentTarget)
    setBusy(true)
    setError(null)
    try {
      await api.manageClientOperatingWorkspace('record_goal_check_in', workbookId, {
        goal_key: goal.goal_key,
        period_start: values.get('period_start'),
        period_end: values.get('period_end'),
        actual_value: values.get('actual_value'),
        status: values.get('status'),
        commentary: values.get('commentary'),
      })
      await load()
    } catch (caught: any) {
      setError(caught?.message || 'The manager check-in could not be saved.')
      setBusy(false)
    }
  }

  const downloadArtifact = async (format: string) => {
    setArtifactBusy(format)
    setError(null)
    try {
      if (format === 'xlsx') {
        const deadline = Date.now() + 90_000
        while (Date.now() < deadline) {
          const response = await api.invokePlatformCapability('create_financial_artifact', workbookId, {
            output_formats: ['xlsx'],
          })
          const delivery = response?.data
          if (delivery?.download_resource?.url) {
            const anchor = document.createElement('a')
            anchor.href = String(delivery.download_resource.url)
            anchor.download = `${String(context?.company?.display_name || 'FuseGrid').replace(/[^a-z0-9 _-]+/gi, '').trim() || 'FuseGrid'}-Governed-Financials.xlsx`
            anchor.rel = 'noopener'
            anchor.click()
            return
          }
          if (response?.status !== 'pending') throw new Error('The governed workbook job did not return a download.')
          await new Promise((resolve) => window.setTimeout(resolve, 1_000))
        }
        throw new Error('The governed workbook is still preparing. Try the download again shortly.')
      }
      const response = await api.invokePlatformCapability('create_financial_artifact', workbookId, {
        comparison: 'mom',
        title: `${context?.company?.display_name || 'Client'} Operating Review`,
        output_formats: ['site', 'pptx', 'pdf'],
        expected_freshness_token: context?.freshness?.token,
      })
      const spec = response?.data as OperatingArtifactSpec
      if (format === 'pptx') await downloadOperatingPptx(spec)
      else if (format === 'pdf') await downloadOperatingPdf(spec)
    } catch (caught: any) {
      setError(caught?.message || 'The evidence-bound file could not be prepared.')
    } finally {
      setArtifactBusy(null)
    }
  }

  const deliverUpdate = async (destination: any) => {
    setArtifactBusy(`deliver-${destination.destination_key}`)
    setError(null)
    try {
      await api.manageClientOperatingWorkspace('deliver_workspace_update', workbookId, {
        destination_key: destination.destination_key,
      })
    } catch (caught: any) {
      setError(caught?.message || 'The manager update could not be delivered.')
    } finally {
      setArtifactBusy(null)
    }
  }

  const renderWidget = (widget: WorkspaceWidget) => {
    if (widget.kind === 'commentary') return <article className="fg-ow-card fg-ow-commentary" key={widget.id}><span>Operating thesis</span><p>{String(widget.markdown || '').replaceAll('**', '')}</p></article>
    if (widget.kind === 'kpi') {
      const item = metric(widget.metric_key || '')
      const comparison = item?.comparisons?.find((candidate: any) => candidate.kind === (widget.comparison || 'mom'))
      const delta = n(comparison?.delta_pct)
      return <article className="fg-ow-card fg-ow-kpi" key={widget.id}><span>{widget.title}</span><strong>{item ? formatWorkspaceMoney(item.latest_value, item.unit, item.currency || context?.currency) : 'Awaiting source'}</strong><small className={delta < 0 ? 'negative' : ''}>{comparison?.complete ? `${delta >= 0 ? '↑' : '↓'} ${Math.abs(delta * 100).toFixed(1)}% vs prior` : 'Current period in progress'}</small></article>
    }
    if (widget.kind === 'attention_feed') return <article className="fg-ow-card" key={widget.id}><header><div><span>Focus</span><h2>{widget.title}</h2></div><b>LIVE</b></header><div className="fg-ow-list">{(context?.material_movements || []).slice(0, 5).map((movement: any, index: number) => <button key={`${movement.metric_id}-${index}`} onClick={() => void investigate(`Explain ${movement.label}`)}><i>{String(index + 1).padStart(2, '0')}</i><span><strong>{movement.label}</strong><small>{String(movement.comparison || '').toUpperCase()} · evidence ready</small></span><em>{formatWorkspaceMoney(movement.delta_abs, metric(movement.metric_id)?.unit, context?.currency)}</em></button>)}</div></article>
    if (widget.kind === 'ask') return <article className="fg-ow-card fg-ow-ask" key={widget.id}><span>AI operating partner</span><h2>{widget.title}</h2><p>Ask in business language. Every answer stays tied to approved data and evidence.</p>{(widget.suggested_prompts || []).map((prompt) => <button key={prompt} disabled={busy} onClick={() => void investigate(prompt)}>{prompt}<b>↗</b></button>)}{answer ? <div className="fg-ow-answer"><small>{answer.prompt}</small><strong>{answer.selected_variance?.label} is the largest current movement.</strong><p>{formatWorkspaceMoney(answer.selected_variance?.delta_abs, metric(answer.selected_variance?.metric_id)?.unit, context?.currency)} change with {answer.supporting_evidence?.length || 0} evidence records.</p></div> : null}</article>
    if (widget.kind === 'goal_scorecard') {
      const visible = goals.filter((goal: any) => !widget.goal_keys?.length || widget.goal_keys.includes(goal.goal_key))
      return <article className="fg-ow-card fg-ow-span" key={widget.id}><header><div><span>Accountability</span><h2>{widget.title}</h2></div><small>{visible.length} active</small></header><div className="fg-ow-goals">{visible.length ? visible.map((goal: any, goalIndex: number) => { const liveMetric = metric(goal.metric_key); const workspaceMetric = workspace?.metrics?.find((item: any) => item.metric_key === goal.metric_key); const unit = liveMetric?.unit ?? workspaceMetric?.unit ?? 'decimal'; const actual = goal.latest_actual?.actual_value ?? goal.latest_check_in?.actual_value ?? liveMetric?.latest_value; const state = goal.latest_check_in?.status ?? goal.latest_actual?.performance_status ?? 'awaiting_update'; const target = goal.target_value != null ? operatingValue(goal.target_value, unit, context?.currency) : `${operatingValue(goal.target_min_value, unit, context?.currency)}–${operatingValue(goal.target_max_value, unit, context?.currency)}`; return <section key={goal.goal_key}><div><b className={`state-${state}`}>{status(state)}</b><small>{goal.cadence}</small></div><h3>{goal.title}</h3><p>{goal.purpose}</p><strong>{operatingValue(actual, unit, context?.currency)}</strong><small>Target {target}</small>{workspace?.role !== 'viewer' ? <details className="fg-ow-checkin" data-fg-tour={goalIndex === 0 ? 'ws.checkin' : undefined}><summary>Post manager check-in</summary><form onSubmit={(event) => void postCheckIn(event, goal)}><label>From<input name="period_start" type="date" required defaultValue={goal.latest_actual?.period_end || context?.as_of} /></label><label>Through<input name="period_end" type="date" required defaultValue={context?.as_of} /></label><label>Actual<input name="actual_value" inputMode="decimal" required /></label><label>Status<select name="status" defaultValue="on_track"><option value="on_track">On track</option><option value="at_risk">At risk</option><option value="off_track">Off track</option><option value="complete">Complete</option></select></label><label className="wide">What changed?<textarea name="commentary" maxLength={2000} /></label><button disabled={busy}>Share update</button></form></details> : null}</section> }) : <div className="fg-ow-empty"><strong>Shape this scorecard with the client.</strong><p>Use the agent to add the operational goals this team actually owns.</p></div>}</div></article>
    }
    if (widget.kind === 'action_list') return <article className="fg-ow-card fg-ow-span" key={widget.id}><header><div><span>Commitments</span><h2>{widget.title}</h2></div></header><div className="fg-ow-actions">{actions.length ? actions.map((item: any) => <button key={item.id} disabled={busy || workspace?.role === 'viewer'} onClick={() => void updateAction(item)}><i className={item.status}>{item.status === 'done' ? '✓' : ''}</i><span><strong>{item.title}</strong><small>{item.description || 'Team commitment'}{item.due_on ? ` · due ${item.due_on}` : ''}</small></span><b>{status(item.status)}</b></button>) : <p>No open commitments. Ask the agent to turn an insight into an owned next step.</p>}</div></article>
    if (widget.kind === 'trend') { const item = metric(widget.metric_keys?.[0] || '') || metrics[0]; const points = item?.trend?.slice(-12) || []; const max = Math.max(...points.map((point: any) => Math.abs(n(point.value))), 1); return <article className="fg-ow-card" key={widget.id}><header><div><span>Trajectory</span><h2>{widget.title}</h2></div><small>{item?.label}</small></header><div className="fg-ow-trend">{points.map((point: any) => <div key={point.period}><i style={{ height: `${Math.max(8, Math.abs(n(point.value)) / max * 100)}%` }} /><small>{String(point.period).slice(5, 7)}</small></div>)}</div></article> }
    if (widget.kind === 'report_downloads') return <article className="fg-ow-card fg-ow-span" key={widget.id}><header><div><span>Meeting output</span><h2>{widget.title}</h2></div></header><div className="fg-ow-report-actions">{(widget.formats || ['xlsx', 'pptx', 'pdf']).map((format) => <button key={format} disabled={artifactBusy != null} onClick={() => void downloadArtifact(format)}><b>{format.toUpperCase()}</b><span>{artifactBusy === format ? 'Preparing live file…' : 'Download evidence-bound file'}</span></button>)}</div>{workspace?.delivery_destinations?.length ? <div className="fg-ow-deliveries"><strong>Send the current manager update</strong>{workspace.delivery_destinations.map((destination: any) => <button key={destination.destination_key} disabled={artifactBusy != null} onClick={() => void deliverUpdate(destination)}>{destination.channel.toUpperCase()} · {destination.label}{artifactBusy === `deliver-${destination.destination_key}` ? ' — sending…' : ''}</button>)}</div> : null}</article>
    if (widget.kind === 'evidence') return <article className="fg-ow-card" key={widget.id}><span>Trust layer</span><h2>{widget.title}</h2><strong className="fg-ow-evidence-count">{context?.evidence?.length || 0}</strong><p>governed evidence records · {context?.reconciliation_state}</p><code>{String(context?.freshness?.token || '').slice(0, 34)}</code></article>
    return null
  }

  if (busy && !context) return <div className="fg-ow-loading" style={{ paddingTop: topInsetPx }}><i /><h1>Preparing the client operating workspace</h1><p>Connecting goals, current results, commitments, and evidence.</p></div>
  if (error && !context) return <div className="fg-ow-loading" style={{ paddingTop: topInsetPx }}><h1>The workspace is unavailable</h1><p>{error}</p><button onClick={() => void load()}>Try again</button></div>

  return <div className="fg-ow" data-fg-workspace-page={pageId} style={{ '--fg-ow-accent': spec?.theme?.accent_color || '#1b4965', paddingTop: topInsetPx } as React.CSSProperties}>
    <aside><div className="fg-ow-client"><i>{String(context?.company?.display_name || 'FG').split(/\s+/).map((word: string) => word[0]).slice(0, 2).join('')}</i><span><strong>{context?.company?.display_name}</strong><small>Client operating workspace</small></span></div><nav>{pages.map((item: any) => <button className={item.id === page?.id ? 'active' : ''} data-fg-tour={`ws.nav.${item.id}`} key={item.id} onClick={() => setPageId(item.id)}>{item.title}</button>)}</nav><div className="fg-ow-advisor"><strong>Tailor with your agent</strong><p>Change pages, goals, language, and manager focus without writing code.</p><button onClick={() => void copyAgentPrompt()}>Copy customization prompt</button></div></aside>
    <main><section className="fg-ow-hero"><div><span>{page?.title || 'Today'} · Live operating view</span><h1 data-fg-tour="ws.title">{spec?.title || `${context?.company?.display_name || 'Client'} Operating Workspace`}</h1><p>The current operating picture, the goals this team owns, and accountable next actions.</p></div><div><span>Current view</span><strong>{context?.reporting_period?.latest_available || context?.as_of}</strong><small>{context?.reporting_period?.completeness === 'closed' ? 'Reconciled period' : context?.reporting_period?.completeness === 'partial' ? 'Month in progress' : 'Latest available · close unconfirmed'}</small>{context?.reporting_period?.latest_reconciled && context.reporting_period.latest_reconciled !== context.reporting_period.latest_available ? <small>Last reconciled {context.reporting_period.latest_reconciled}</small> : null}</div></section>{error ? <div className="fg-ow-error">{error}<button onClick={() => setError(null)}>Dismiss</button></div> : null}<div className="fg-ow-content">{page?.sections?.map((section: any) => <section className={`layout-${section.layout}`} key={section.id}>{section.widgets.map(renderWidget)}</section>)}</div><footer><span>● Live with QuickBooks · {context?.freshness?.source_as_of}</span><button onClick={() => void load()}>Refresh</button><code>{String(context?.freshness?.token || '').slice(0, 28)}</code></footer></main>
  </div>
}
